import React from "react";
import { Input, Button } from "@heroui/react";
import { IMAGES } from "@/localConstants/images";

interface BridgeAmountInputProps {
  amount: string;
  onAmountChange: (value: string) => void;
  onPercentageClick: (percentage: number) => void;
  isConnected: boolean;
  hasInsufficientBalance: boolean;
  isBelowMinimum: boolean;
  isAboveMaximum: boolean;
  currentBalance: string;
  limits?: {
    assetFrom: {
      amountMin: string;
      amountMax: string;
    };
  };
  limitsError: any;
}

export const BridgeAmountInput: React.FC<BridgeAmountInputProps> = ({
  amount,
  onAmountChange,
  onPercentageClick,
  isConnected,
  hasInsufficientBalance,
  isBelowMinimum,
  isAboveMaximum,
  currentBalance,
  limits,
  limitsError,
}) => {
  return (
    <div className="space-y-3">
      <Input
        type="number"
        placeholder="Add amount"
        value={amount}
        onValueChange={onAmountChange}
        variant="flat"
        size="lg"
        endContent={
          <div className="flex items-center gap-1 sm:gap-2 mr-2 sm:mr-3">
            <img
              src={IMAGES.A1X_LOGO}
              alt="A1X"
              className="w-5 h-5 sm:w-6 sm:h-6"
            />
            <span className="text-white font-light text-sm sm:text-base">A1X</span>
          </div>
        }
        classNames={{
          input:
            "text-xl sm:text-2xl font-bold text-white bg-transparent font-roboto placeholder:font-normal placeholder:text-gray-400",
          inputWrapper:
            "bg-neutral-900 border border-neutral-700 h-12 sm:h-14",
        }}
      />
      
      {/* Validation error messages - prioritized to show only one at a time */}
      {amount && isConnected && (
        <>
          {hasInsufficientBalance ? (
            <p className="text-xs text-red-500 mt-1 font-roboto">
              Insufficient balance. Available: {currentBalance} A1X
            </p>
          ) : isBelowMinimum && limits ? (
            <p className="text-xs text-red-500 mt-1 font-roboto">
              Amount below minimum. Minimum: {limits.assetFrom.amountMin} A1X
            </p>
          ) : isAboveMaximum && limits ? (
            <p className="text-xs text-red-500 mt-1 font-roboto">
              Amount above maximum. Maximum: {limits.assetFrom.amountMax} A1X
            </p>
          ) : null}
        </>
      )}
      
      {/* Limits error - only show if no amount validation errors */}
      {limitsError && (!amount || !isConnected || (!hasInsufficientBalance && !isBelowMinimum && !isAboveMaximum)) && (
        <p className="text-xs text-red-500 mt-1 font-roboto">
          Unable to load limits. Please try again.
        </p>
      )}

      {/* Percentage Buttons */}
      {isConnected && (
        <div className="flex gap-1 sm:gap-2">
          {[25, 50, 75, 100].map((percentage) => (
            <Button
              key={percentage}
              variant="bordered"
              size="sm"
              className="flex-1 border-neutral-500 border-1 bg-transparent w-full m-0 hover:bg-neutral-700 text-xs sm:text-sm h-8 sm:h-10"
              onPress={() => onPercentageClick(percentage)}
            >
              {percentage === 100 ? "MAX" : `${percentage}%`}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};